import { z } from "zod";

type ParseIntegerOptions = Partial<{
  readonly strict: boolean;
  readonly errorMessage: string;
}>;

type ParseIntegerRT<O extends ParseIntegerOptions> = O extends {
  strict: false;
}
  ? null | number
  : number;

/**
 * Converts the provided value to an integer number if the provided value is a string form of a
 * valid integer.  Otherwise, if the provided value is a valid integer, that value is returned.
 * Finally, throws an error if the value is neither a valid integer or a string form of a valid
 * integer.
 *
 * Note: Floats will not be treated as valid integers.
 *
 * @param {unknown} value  The value that should be converted to an integer.
 * @param {ParseIntegerOptions} options
 *   The options that should dictate the return type and error handling behavior.
 *
 * @example
 * // Returns null
 * toInteger("1.25", { strict: false });
 *
 * @example
 * // Throws an error
 * toInteger("1.25");
 *
 * @example
 * // Returns 5
 * toInteger("5");
 */
export const parseInteger = <O extends ParseIntegerOptions>(
  value: unknown,
  options: O,
): ParseIntegerRT<O> => {
  if (
    (typeof value === "string" && value.replaceAll(" ", "") === "") ||
    !["number", "string"].includes(typeof value)
  ) {
    if (options.strict === false) {
      return null as ParseIntegerRT<O>;
    }
    const message =
      options.errorMessage ||
      `The value '${value}' cannot be converted to a valid integer!`;
    throw new Error(message);
  }
  const schema = z.union([z.number().int(), z.coerce.number().int()]);
  const parsed = schema.safeParse(value);
  if (parsed.success) {
    return parsed.data as ParseIntegerRT<O>;
  } else if (options.strict === false) {
    return null as ParseIntegerRT<O>;
  }
  const message =
    options.errorMessage ||
    `The value '${value}' cannot be converted to a valid integer!`;
  throw new Error(message);
};
